import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../index';
import { KidState } from './types';
import {
  calcKPayeeValue,
  calcKUmbrellaValue,
  previewForCis,
  previewForPaye,
  previewForUmbrella,
  sendEmailForCis,
  sendEmailForPaye,
  sendEmailForUmbrella,
  getKidHistory,
  resendEmail
} from './actions';

const initialState: KidState = {
  kidValue: null,
  visibleAction: false,
  visibleDetail: false,
  loading: false,
  kidHistories: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  kidHistoryDetail: null,
  previewFormData: null
};

const kidSlice = createSlice({
  name: 'kid',
  initialState,
  reducers: {
    setKidValue: (state, action: PayloadAction<KidState['kidValue']>) => {
      state.kidValue = action.payload;
    },
    hideKIDDetail: (state) => {
      state.visibleDetail = false;
      state.kidHistoryDetail = null;
    },
    showKIDDetail: (state, action: PayloadAction<KidState['kidHistoryDetail']>) => {
      state.kidHistoryDetail = action.payload;
      state.visibleDetail = true;
    },
    onPreviewKIDSubcontractor: (
      state,
      action: PayloadAction<KidState['kidValue']>,
    ) => {
      state.kidValue = action.payload;
      state.visibleAction = true;
    },
    setPreviewFormData: (state, action: PayloadAction<any>) => {
      state.previewFormData = action.payload;
    },
    setPreviewData: (
      state,
      action: PayloadAction<{
        formData: any;
      }>,
    ) => {
      state.previewFormData = action.payload.formData;
    },
    resetPreview: (state) => {
      state.previewFormData = null;
      state.kidValue = null;
      state.visibleAction = false;
      state.visibleDetail = false;
      state.kidHistoryDetail = null;
    },
  },
  extraReducers: (builder) => {
    // Add any async actions here if needed
    builder.addCase(calcKPayeeValue.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(calcKPayeeValue.fulfilled, (state, action) => {
      const calculate = action.payload;
      state.loading = false;
      state.kidValue = !!calculate?.errors?.length ? null: action.payload;
      state.visibleAction = true;
    });
    builder.addCase(calcKPayeeValue.rejected, (state) => {
      state.loading = false;
      // NX-5849: clear the previous result. Without this a failed
      // recalculation leaves the figures from the last successful one on
      // screen, with Preview and Send still enabled against stale numbers.
      state.kidValue = null;
      state.visibleAction = false;
    });
    builder.addCase(calcKUmbrellaValue.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(calcKUmbrellaValue.fulfilled, (state, action) => {
      const calculate = action.payload;
      state.loading = false;
      state.kidValue = !!calculate?.errors?.length ? null: action.payload;
      state.visibleAction = true;
    });
    builder.addCase(calcKUmbrellaValue.rejected, (state) => {
      state.loading = false;
      state.kidValue = null;
      state.visibleAction = false;
    });
    //preview
    builder.addCase(previewForCis.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(previewForCis.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(previewForCis.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(previewForPaye.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(previewForPaye.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(previewForPaye.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(previewForUmbrella.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(previewForUmbrella.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(previewForUmbrella.rejected, (state) => {
      state.loading = false;
    });
    // send
    builder.addCase(sendEmailForCis.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendEmailForCis.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(sendEmailForCis.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(sendEmailForPaye.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendEmailForPaye.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(sendEmailForPaye.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(sendEmailForUmbrella.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendEmailForUmbrella.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(sendEmailForUmbrella.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(resendEmail.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(resendEmail.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(resendEmail.rejected, (state) => {
      state.loading = false;
    });
    //
    builder.addCase(getKidHistory.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getKidHistory.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getKidHistory.fulfilled, (state, action) => {
      state.loading = false;
      state.kidHistories.list = action.payload.data;
      state.kidHistories.pagination = action.payload.pagination;
      state.kidHistories.queryParams = (action.payload as any).queryParams;
    });
  },
});

// Export actions
export const kidAction = {
  ...kidSlice.actions,
  calcKUmbrellaValue,
  previewForCis,
  previewForPaye,
  previewForUmbrella,
  sendEmailForCis,
  sendEmailForPaye,
  sendEmailForUmbrella,
  getKidHistory,
  resendEmail
};

// Export selectors
export const selectKidValue = (state: RootState) => state.kid.kidValue;
export const selectActionVisible = (state: RootState) => state.kid.visibleAction;
export const selectPreviewFormData = (state: RootState) =>
  state.kid.previewFormData;

// Export reducer
export const kidReducer = kidSlice.reducer;
